// @flow
/** @file API related utilities */

import { signOut } from 'aws-amplify/auth';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';

import { log } from './util';

/**
 * Create default headers for API communication
 *
 * @param {object} headerData - Data for headers
 * @param  {string} headerData.authToken - Auth token
 * @param  {string} headerData.requestId - Optional request ID
 * @returns {object} - Object of header data
 */
export const createDefaultHeaders = ({
  authToken,
  requestId,
}: {
  authToken: string;
  requestId?: string;
}) =>
  new Headers({
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
    'request-id': requestId || uuid(),
  });

export const createAxiosHeaders = ({
  authToken,
  requestId,
}: {
  authToken: string;
  requestId?: string;
}) => ({
  Authorization: `Bearer ${authToken}`,
  'Content-Type': 'application/json',
  'request-id': requestId || uuid(),
});

/**
 * Throw response if it contains error
 * Also handles "No accounts found for user" by logging out
 *
 * @param {Response} resp - Response
 * @returns {Response} - Passed in response
 */
export const throwIfError = async (resp: Response): Promise<Response> => {
  if (!resp.ok || resp.status < 200 || resp.status >= 400) {
    // Check if this is a "No accounts found for user" error
    const errorText = await resp.text();

    if (errorText.includes('No accounts found for user')) {
      toast.info('Your account access has been removed. Redirecting to login...', {
        closeButton: true,
        autoClose: false,
      });
      log.info('API', 'User has no accounts, logging out');

      // Clear admin info from localStorage
      window.localStorage.removeItem('adminInfo');

      // Sign out and redirect to login
      try {
        await signOut();
      } catch (signOutErr) {
        console.error('Error during sign out:', signOutErr);
      } finally {
        window.location.assign('/');
      }

      // Throw a special error to prevent further processing
      throw new Error('No accounts found - user logged out');
    }

    // For other errors, re-create a Response-like object with the text we already read
    const errorResp = new Response(errorText, {
      status: resp.status,
      statusText: resp.statusText,
      headers: resp.headers,
    });
    throw errorResp;
  }

  return resp;
};

/**
 * Throws Error if response is not ok, else return response json
 * Retrieves the error text before throwing Error
 *
 * @param {Response} resp - Response
 * @returns {any} - Result of json()
 */
export const getJSONOrThrow = (resp: Response) => {
  if (!resp.ok || resp.status < 200 || resp.status >= 400) {
    return resp.text().then((text) => {
      throw new Error(text);
    });
  }

  return resp.json();
};
